import { AddOutlined, ContentCopy, DeleteOutlineOutlined, Edit, Power, PowerOff } from "@mui/icons-material";
import { Box, Button, IconButton } from "@mui/material";
import {
  MRT_ColumnDef,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useSysCfgQuery } from "@/api/hooks/sys-cfg-hooks";
import { useCfgPerm, useIsOwnerOrSuperAdmin } from "@/auth/hooks";
import ApplyAuthGroupsTableControl from "@/components/apply-auth-groups-table-control";
import BulkOperationReportModal from "@/components/bulk-operation-report-modal";
import CommonConfigWriteModal from "@/components/config-modals/common-config-write-modal";

import { useTableSettings } from "@/hooks/use-table-settings";
import { assembleNamesFilter } from "@/lib/cfg-utils";
import {
  useConfigChangeEnabled,
  useDeleteConfigs,
  useSaveConfig,
  useUpdateConfig,
} from "@/module/room-configs-module/hooks/room-configs-hooks";
import { ResultDtoLong, SysCfgCreateRequestDto, SysCfgRegularDto } from "@/shared/api";
import Popconfirm from "@/components/popcofirm";
import Checkbox from "@mui/material/Checkbox";


const CommonConfigTable = ({ room, cfg }: { room: string; cfg: string }) => {
  const { t } = useTranslation();
  const {
    data: configsRs,
    isLoading: configsIsLoading,
    refetch: refetchConfigs,
  } = useSysCfgQuery({
    domain: room,
    config_name: cfg,
    sort: [{ key: "created_at", mode: "DESC" }],
  });

  const cfgPerm = useCfgPerm({
    roomName: room,
    cfgName: cfg,
  });

  const configs = React.useMemo(() => {
    return configsRs?.data?.content ?? [];
  }, [configsRs]);

  const isOwner = useIsOwnerOrSuperAdmin();

  const [bulkOperationResult, setBulkOperationResult] = useState<ResultDtoLong[] | undefined>(
    undefined
  );

  const {saveConfigMutation: handleClickSaveNewConfig, isPending: isSaveConfigPending} = useSaveConfig({
    roomName: room,
    cfgName: cfg,
    onSuccessCallback: () => {
      void refetchConfigs();
      closeAddConfig();
    },
  });

  const {updateConfigMutation: handleClickUpdateConfig, isPending: isUpdateConfigPending} = useUpdateConfig({
    roomName: room,
    cfgName: cfg,
    onSuccessCallback: () => {
      void refetchConfigs();
      setEditingConfig(undefined);
    },
  });

  const handleChangeEnableBulk = useConfigChangeEnabled({
    roomName: room,
    cfgName: cfg,
    onSuccessCallback: (table) => {
      void refetchConfigs();
      table.resetRowSelection();
    },
  });

  const handleDeleteConfigs = useDeleteConfigs({
    roomName: room,
    cfgName: cfg,
    onSuccessCallback: (table, response) => {
      setBulkOperationResult([response.data]);
      void refetchConfigs();
      table.resetRowSelection();
    },
  });

  //This logic for reset row selection while using filtering/grouping
  const [editingConfig, setEditingConfig] = useState<SysCfgRegularDto | undefined>(undefined);
  const [addConfig, setAddConfig] = useState<boolean>(false);
  const [addingConfig, setAddingConfig] = useState<SysCfgCreateRequestDto | undefined>(
    undefined
  );

  function closeAddConfig() {
    setAddingConfig(undefined);
    setAddConfig(false);
  }

  function handleClickAddConfig() {
    setAddConfig(true);
  }

  const columns = useMemo<MRT_ColumnDef<SysCfgRegularDto>[]>(
    () => [
      {
        header: t("commonTable.enable"),
        filterFn: "contains",
        enableEditing: false,
        accessorFn: (row) => row.enabled ?? "-",
        Cell: ({ row }) => <Checkbox sx={{ padding: 0 }} checked={row.original.enabled} disabled />,
      },
      {
        header: t("commonTable.name"),
        accessorKey: "name",
        filterFn: "contains",
      },
    ],
    [t]
  );

  const { defaultProps, state, columnFilters, grouping, globalFilter } =
    useTableSettings<SysCfgRegularDto>("common");

  const table = useMaterialReactTable({
    data: configs ?? [],
    columns: columns,
    getRowId: (originalRow) => originalRow.name,
    ...defaultProps,
    initialState: {
      ...defaultProps.initialState,
      sorting: [{ id: "name", desc: false }],
    },
    selectAllMode: "all",
    enableSelectAll: true,
    enableRowSelection: true,
    enableGrouping: true,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enableRowActions: true,
    enablePagination: true,
    enableStickyHeader: true,
    enableSorting: true,
    state: {
      ...state,
      sorting: state.sorting.length > 0 ? state.sorting : [{ id: "name", desc: false }],
      isLoading: configsIsLoading,
    },
    renderTopToolbarCustomActions: () => {
      const selectedCount = table.getSelectedRowModel().rows.length;
      return (
        <div style={{ height: "40px", display: "flex", alignItems: "center"}}>
          {cfgPerm.write && (
            <Button onClick={() => handleClickAddConfig()}>
              <AddOutlined />
              {t("new")}
            </Button>
          )}
          {selectedCount > 0 && (
            <div style={{ marginLeft: 30, display: "flex" }}> 
              {cfgPerm.execute && (
                <IconButton
                  size="small"
                  style={{ fontSize: "15px" }}
                  color="success"
                  onClick={() => handleChangeEnableBulk(table, true)}
                >
                  <Power /> {t("enable")}
                </IconButton>
              )}
              {cfgPerm.execute && (
                <IconButton
                  size="small"
                  style={{ fontSize: "15px" }}
                  color="error"
                  onClick={() => handleChangeEnableBulk(table, false)}
                >
                  <PowerOff /> {t("disable")}
                </IconButton>
              )}

              {cfgPerm.delete && (
                <Popconfirm
                  title={t("operationResult")}
                  description={t("areYouSureToDeleteItems")}
                  onConfirm={() => handleDeleteConfigs(table)}
                >
                  <IconButton size="small" style={{ fontSize: "15px" }} color="error">
                    <DeleteOutlineOutlined /> {t("delete")}
                  </IconButton>
                </Popconfirm>
              )}

              {isOwner && (
                <ApplyAuthGroupsTableControl
                  domain={room}
                  cfgName={cfg}
                  table={table}
                  filters={assembleNamesFilter(table)}
                  totalElementsCount={selectedCount}
                  onSuccess={() => void refetchConfigs()}
                />
              )}
            </div>
          )}
        </div>
      );
    },
    renderRowActions: ({ row }) => (
      <Box>
        <IconButton onClick={() => setEditingConfig({ ...row.original })}>
          <Edit />
        </IconButton>
        {cfgPerm.write && (
          <IconButton
            onClick={() => {
              setAddConfig(true);
              setAddingConfig({
                ...row.original,
                name: "",
                auth_groups: row.original.auth_groups ?? new Set(),
              });
            }}
          >
            <ContentCopy />
          </IconButton>
        )}
      </Box>
    ),
  });

  useEffect(() => {
    table.resetRowSelection();
  }, [columnFilters, grouping, globalFilter, table]);

  return (
    <>
      <MaterialReactTable table={table} />

      {addConfig && (
        <CommonConfigWriteModal
          mode="add"
          isOpen={addConfig}
          defaultValues={addingConfig}
          onCancel={() => closeAddConfig()}
          onSave={(config) => handleClickSaveNewConfig(config)}
          disabled={!cfgPerm.write}
          confirmLoading={isSaveConfigPending}
          title={t("addNewCommonConfigNameTemplate", {configName: t(cfg)})}
        />
      )}

      {editingConfig && (
        <CommonConfigWriteModal
          mode="edit"
          isOpen={!!editingConfig}
          defaultValues={editingConfig}
          onCancel={() => setEditingConfig(undefined)}
          onSave={(config) => handleClickUpdateConfig(config.name, config)}
          confirmLoading={isUpdateConfigPending}
          disabled={!cfgPerm.write}
          title={t("addNewCommonConfigNameTemplate", {configName: t(cfg)})}
        />
      )}

      {bulkOperationResult && (
        <BulkOperationReportModal
          resultList={bulkOperationResult}
          title={t("operationResult")}
          showProblematic
          showSuccess
          onCancel={() => setBulkOperationResult(undefined)}
          open={!!bulkOperationResult}
        />
      )}
    </>
  );
};

export default CommonConfigTable;
